import { PluginListenerHandle, registerPlugin } from '@capacitor/core';

/**
 * Native plugin proxies must be registered once per JavaScript runtime.
 * Re-registering them in every component floods the Capacitor bridge during
 * cold start and produces "already registered" warnings.
 */
export interface NativeLiquidGlassPlugin {
  setRegion(options: Record<string, any>): Promise<{ available: boolean }>;
  setChromeSuppressed(options: { suppressed: boolean }): Promise<{ available: boolean }>;
  setPublicationInfoSuppressed(options: { suppressed: boolean }): Promise<{ available: boolean }>;
  restoreRegions(): Promise<{ available: boolean }>;
  markWebViewReady(): Promise<{ ready: boolean }>;
  dismissStartupFeedback(): Promise<{ available: boolean; dismissed: boolean }>;
  /**
   * Returns the live UIKit footer position in WKWebView coordinates.  This is
   * intentionally geometry, not a hard-coded dock height, so web overlays can
   * align to the native footer just as they do to an Angular footer.
   */
  getFooterMetrics(): Promise<{
    available: boolean;
    visible: boolean;
    footerTop?: number;
    webViewHeight?: number;
  }>;
  /** Returns active native glass bounds in WKWebView coordinates. */
  getChromeMetrics(): Promise<{
    available: boolean;
    headerVisible?: boolean;
    headerBottom?: number;
    footerVisible?: boolean;
    footerTop?: number;
    webViewHeight?: number;
  }>;
  addListener(
    eventName: string,
    listener: (event: any) => void
  ): Promise<PluginListenerHandle>;
}

export interface NativeHapticsPlugin {
  impact(options: { style: string }): Promise<void>;
  selection(): Promise<void>;
  notification(options: { type: 'SUCCESS' | 'WARNING' | 'ERROR' }): Promise<void>;
}

export interface StoryMediaAsset {
  id: string;
  type: 'image' | 'video';
  duration: number;
  thumbnail: string;
  isLive: boolean;
}

export interface NativeStoryMediaPlugin {
  latestThumbnails(): Promise<{ photo: string; video: string }>;
  listAlbums(): Promise<{ albums: Array<{ id: string; name: string; count?: number }> }>;
  listAssets(options: {
    filter: 'all' | 'photos' | 'videos' | 'live';
    albumId?: string;
    offset: number;
    limit: number;
  }): Promise<{ assets: StoryMediaAsset[]; hasMore: boolean }>;
  exportAsset(options: { id: string }): Promise<{
    url: string;
    mimeType: string;
    name: string;
    type: 'image' | 'video';
    size: number;
  }>;
  readExportChunk(options: { url: string; offset: number; length: number }): Promise<{
    data: string;
    bytesRead: number;
  }>;
  releaseExport(options: { url: string }): Promise<void>;
}

export interface NativeShazamPlugin {
  isAvailable(): Promise<{ available: boolean; microphonePermission?: string }>;
  recognize(options?: { developerToken?: string }): Promise<{
    title: string;
    artist?: string;
    subtitle?: string;
    artworkUrl?: string;
    genres?: string[];
    isrc?: string;
    shazamUrl?: string;
    appleMusicUrl?: string;
    explicit?: boolean;
  }>;
  stop(): Promise<{ stopped: boolean }>;
}

/**
 * Foreground BLE handoff for Hubble. The advertised value is an opaque,
 * short-lived server token — never a wallet ID or balance.
 */
export interface NativeNearbyPlugin {
  isAvailable(): Promise<{ available: boolean }>;
  advertise(options: { token: string }): Promise<{ started: boolean }>;
  scan(options?: { timeoutMs?: number }): Promise<{ token: string }>;
  stop(): Promise<{ stopped: boolean }>;
}

export interface NativeTapToPayPlugin {
  isAvailable(): Promise<{ available: boolean; reason?: string }>;
  branding(): Promise<{ symbolName: string; imageDataUrl?: string }>;
  configure(options: {
    connectionTokenEndpoint: string;
    authorization: string;
    locationId: string;
  }): Promise<{ configured: boolean }>;
  getAccountStatus(): Promise<{ linked: boolean | null }>;
  enable(): Promise<{ linked: boolean; connected: boolean }>;
  reviewTerms(): Promise<{ linked: boolean; connected: boolean }>;
  showMerchantEducation(): Promise<{ presented: boolean }>;
  warmUp(): Promise<{ linked: boolean; connected: boolean; warmedUp?: boolean }>;
  processPayment(options: { clientSecret: string }): Promise<{
    paymentIntentId: string;
    status: string;
  }>;
  disconnect(): Promise<{ disconnected: boolean }>;
  cancel(): Promise<{ cancelled: boolean }>;
  addListener(
    eventName: 'tapToPayStatus',
    listener: (event: { state: string; message?: string; progress?: number }) => void
  ): Promise<PluginListenerHandle>;
}

export const NativeLiquidGlass = registerPlugin<NativeLiquidGlassPlugin>('NativeLiquidGlass');
export const NativeHaptics = registerPlugin<NativeHapticsPlugin>('Haptics');
export const NativeStoryMedia = registerPlugin<NativeStoryMediaPlugin>('NativeStoryMedia');
export const NativeShazam = registerPlugin<NativeShazamPlugin>('NativeShazam');
export const NativeNearby = registerPlugin<NativeNearbyPlugin>('NativeNearby');
export const NativeTapToPay = registerPlugin<NativeTapToPayPlugin>('NativeTapToPay');

export const NativeAudioPlayback = registerPlugin<{
  isOtherAudioPlaying(): Promise<{ playing: boolean; shouldBeSilenced?: boolean }>;
}>('NativeAudioPlayback');

export interface NativeLiveActivitiesPlugin {
  isAvailable(): Promise<{ available: boolean }>;
  upsert(options: {
    id: string;
    kind: 'delivery' | 'event' | 'voice' | 'upload' | 'music';
    title: string;
    status: string;
    detail?: string;
    subtitle?: string;
    progress?: number;
    durationSeconds?: number;
    isPlaying?: boolean;
    destination: string;
  }): Promise<{ activityId: string }>;
  end(options: { id: string }): Promise<{ ended: boolean }>;
}

export const NativeLiveActivities = registerPlugin<NativeLiveActivitiesPlugin>('NativeLiveActivities');

export interface NativeSpotlightPlugin {
  indexItems(options: { items: Array<{ id: string; title: string; summary?: string }> }): Promise<{ indexed: number }>;
  takePendingURL(): Promise<{ url: string }>;
}

export const NativeSpotlight = registerPlugin<NativeSpotlightPlugin>('NativeSpotlight');

export interface NativeCallEvent {
  action: 'incoming' | 'answer' | 'video' | 'end' | 'mute' | 'audioActivated' | 'audioRouteChanged';
  uuid?: string;
  meeting_id?: string;
  caller_name?: string;
  has_video?: boolean;
  native_audio?: boolean;
  native_audio_failed?: boolean;
  muted?: boolean;
  speaker_enabled?: boolean;
}
export const NativeCalls = registerPlugin<{
  getPending(): Promise<{ calls: NativeCallEvent[] }>;
  refreshRegistration(): Promise<void>;
  openIncoming(options: { meeting_id: string; answer?: boolean }): Promise<void>;
  unregister(): Promise<void>;
  end(options: { uuid: string }): Promise<void>;
  mediaConnected(options: { uuid: string }): Promise<void>;
  prepareAudio(options: { uuid: string }): Promise<void>;
  completeAudioHandoff(options: { uuid: string }): Promise<void>;
  resumeNativeAudio(options: { uuid: string }): Promise<void>;
  handoffToWeb(options: { uuid: string }): Promise<void>;
  setMuted(options: { uuid: string; muted: boolean }): Promise<void>;
  setSpeaker(options: { uuid: string; speaker_enabled: boolean }): Promise<void>;
  addListener(event: 'callEvent', listener: (event: NativeCallEvent) => void): Promise<PluginListenerHandle>;
}>('NativeCalls');
